import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Menu, X, ChevronLeft, ChevronRight, LogOut, Sun, Moon, Settings } from 'lucide-react';

const COLLAPSE_KEY = 'sf_admin_sidebar_collapsed';

// Top-level admin nav — a permanent collapsible column on desktop, an
// off-canvas drawer (triggered by a slim top bar) on mobile. Sub-tabs
// (Content, Reviewer Management) stay on the existing horizontal TabBar,
// this only replaces the old admin-navbar + top TabBar shell.
// The notification bell deliberately does NOT live here — it's rendered
// fixed at the top-right of the page by AdminPanel.js instead, since this
// rail sits at the far left of the screen and a right-anchored dropdown
// rendered from here has nowhere to expand into (was clipped off-screen).
export default function AdminSidebar({ tabs, active, onChange, onSettingsClick, settingsActive }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === 'true');
  const [mobileOpen, setMobileOpen] = useState(false);
  const isSuperAdmin = user?.role === 'super_admin';

  const toggleCollapsed = () => {
    setCollapsed(c => {
      localStorage.setItem(COLLAPSE_KEY, String(!c));
      return !c;
    });
  };

  const handleSignOut = () => { logout(); navigate('/'); };

  // Alt+B fired from AdminPanel.js's global shortcut handler (same
  // custom-event pattern already used for Ctrl+S / 'sf-save-review') —
  // toggles the desktop rail collapse, or the mobile off-canvas drawer,
  // whichever is actually visible at the current width. Deliberately Alt+B,
  // not Ctrl+B — Chrome/extensions can silently swallow Ctrl+B before the
  // page ever sees the keydown (confirmed via a user report), Alt+B doesn't
  // collide with anything.
  useEffect(() => {
    const onToggle = () => {
      if (window.innerWidth <= 860) setMobileOpen(o => !o);
      else toggleCollapsed();
    };
    window.addEventListener('sf-toggle-sidebar', onToggle);
    return () => window.removeEventListener('sf-toggle-sidebar', onToggle);
  }, []);

  // `isCollapsed` defaults to the persisted desktop preference but the
  // mobile drawer always passes `false` explicitly — it's a separate
  // rendering context (an off-canvas overlay, not the permanent rail) and
  // should never inherit the desktop collapse state.
  const navItem = (t, onClick, isCollapsed = collapsed) => (
    <button key={t.id} onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 11, width: '100%', padding: '10px 13px',
        borderRadius: 'var(--r-md)', border: 'none', cursor: 'pointer', fontFamily: 'var(--font)',
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        background: active === t.id ? 'var(--pink-glow)' : 'none',
        color: active === t.id ? 'var(--pink)' : 'var(--text-secondary)',
        fontWeight: active === t.id ? 800 : 600, fontSize: 13.5,
      }}>
      {/* Collapsed rail is narrow — icon + badge side by side collided and
          misaligned there, so collapsed swaps the icon out for the badge
          entirely when one exists, rather than trying to fit both. Expanded
          view is unaffected — plenty of room for icon, label, and badge. */}
      {isCollapsed && t.badge > 0 ? (
        <span style={{ background: 'var(--pink)', color: '#fff', fontSize: 10, fontWeight: 800, borderRadius: 9, minWidth: 18, height: 18, padding: '0 5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {t.badge > 99 ? '99+' : t.badge}
        </span>
      ) : (
        <span style={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{t.icon}</span>
      )}
      {!isCollapsed && <span style={{ whiteSpace: 'nowrap', flex: 1, textAlign: 'left' }}>{t.label}</span>}
      {!isCollapsed && t.badge > 0 && (
        <span style={{ background: 'var(--pink)', color: '#fff', fontSize: 9, fontWeight: 800, borderRadius: 8, minWidth: 16, height: 16, padding: '0 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {t.badge > 99 ? '99+' : t.badge}
        </span>
      )}
    </button>
  );

  const footer = (onNavClick, isCollapsed = collapsed) => (
    <div style={{ padding: '12px 10px', borderTop: '1.5px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', justifyContent: isCollapsed ? 'center' : 'flex-start' }}>
        {user?.avatar_url
          ? <img src={user.avatar_url} alt="" style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0 }} />
          : <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--pink)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0 }}>{(user?.name || 'A')[0].toUpperCase()}</div>
        }
        {!isCollapsed && (
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name?.split(' ')[0]}</div>
            <div style={{ fontSize: 10, fontWeight: 800, color: user?.role === 'super_admin' ? 'var(--pink)' : 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
            </div>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, flexDirection: isCollapsed ? 'column' : 'row' }}>
        {isSuperAdmin && onSettingsClick && (
          <button onClick={() => { onNavClick && onNavClick(); onSettingsClick(); }} title="Settings"
            style={{ flex: isCollapsed ? 'none' : 1, width: isCollapsed ? '100%' : 'auto', height: 30, borderRadius: 'var(--r-sm)', background: settingsActive ? 'var(--pink-glow)' : 'var(--bg-elevated)', border: `1.5px solid ${settingsActive ? 'var(--pink-glow-strong)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <Settings size={13} color={settingsActive ? 'var(--pink)' : 'var(--text-secondary)'} />
          </button>
        )}
        <button onClick={toggle} title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
          style={{ flex: isCollapsed ? 'none' : 1, width: isCollapsed ? '100%' : 'auto', height: 30, borderRadius: 'var(--r-sm)', background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          {theme === 'dark' ? <Sun size={13} color="var(--text-secondary)" /> : <Moon size={13} color="var(--text-secondary)" />}
        </button>
        <button onClick={() => { onNavClick && onNavClick(); handleSignOut(); }} title="Sign out"
          style={{ flex: isCollapsed ? 'none' : 1, width: isCollapsed ? '100%' : 'auto', height: 30, borderRadius: 'var(--r-sm)', background: 'none', border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)' }}>
          <LogOut size={13} />
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* ── Desktop permanent sidebar ── */}
      <div className="admin-sidebar-desktop" style={{
        width: collapsed ? 72 : 244, flexShrink: 0, position: 'sticky', top: 0, height: '100vh',
        background: 'var(--bg-card)', borderRight: '1.5px solid var(--border)',
        display: 'flex', flexDirection: 'column', transition: 'width 0.15s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between', padding: '16px 14px', borderBottom: '1.5px solid var(--border)', minHeight: 58 }}>
          {!collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <img src="/logo.png" alt="Shortflix" style={{ height: 22 }} onError={e => { e.target.style.display = 'none'; }} />
            </div>
          )}
          <button onClick={toggleCollapsed} title={collapsed ? 'Expand' : 'Collapse'}
            style={{ width: 30, height: 30, borderRadius: 'var(--r-sm)', background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, color: 'var(--text-muted)' }}>
            {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
        </div>

        <nav style={{ padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: 3, flex: 1, overflowY: 'auto' }}>
          {tabs.map(t => navItem(t, () => onChange(t.id)))}
        </nav>

        {footer()}
      </div>

      {/* ── Mobile top bar + drawer ──
          Topbar is deliberately minimal (hamburger + logo only) — profile,
          Settings, theme, and sign-out all live in the drawer's footer
          instead, mirroring the desktop sidebar's own layout exactly rather
          than duplicating those controls in both places. */}
      <div className="admin-sidebar-mobile-topbar" style={{ alignItems: 'center', gap: 10, padding: '0 14px', height: 54, background: 'var(--nav-bg)', backdropFilter: 'blur(18px)', borderBottom: '1.5px solid var(--border)', position: 'sticky', top: 0, zIndex: 100 }}>
        <button onClick={() => setMobileOpen(true)} style={{ width: 30, height: 30, borderRadius: 'var(--r-sm)', background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
          <Menu size={15} color="var(--text-secondary)" />
        </button>
        <img src="/logo.png" alt="Shortflix" style={{ height: 22 }} onError={e => { e.target.style.display = 'none'; }} />
      </div>

      {mobileOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'var(--overlay)', zIndex: 200, display: 'flex', justifyContent: 'flex-start' }}
          onClick={e => e.target === e.currentTarget && setMobileOpen(false)}>
          <div style={{ width: 240, height: '100%', background: 'var(--bg-card)', borderRight: '1.5px solid var(--border)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 12px', borderBottom: '1.5px solid var(--border)', minHeight: 54 }}>
              <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Menu</span>
              <button onClick={() => setMobileOpen(false)} style={{ width: 26, height: 26, borderRadius: 'var(--r-sm)', background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-muted)' }}>
                <X size={13} />
              </button>
            </div>
            <nav style={{ padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 2, flex: 1, overflowY: 'auto' }}>
              {tabs.map(t => navItem(t, () => { onChange(t.id); setMobileOpen(false); }, false))}
            </nav>
            {footer(() => setMobileOpen(false), false)}
          </div>
        </div>
      )}
    </>
  );
}
